/**
 * ResponseProcessor.js
 * 
 * Service for processing AI responses into actionable operations.
 * Parses, validates, and applies AI responses to the project.
 */

class ResponseProcessor {
  constructor(storageManager) {
    this.storage = storageManager;
  }
  
  /**
   * Process an AI response
   * @param {string} jsonString - Raw JSON response from AI
   * @param {string} currentPhase - Current project phase
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async processResponse(jsonString, currentPhase, projectId) {
    try {
      // Attempt to parse the JSON response
      const response = JSON.parse(jsonString);
      
      // Validate schema
      if (!this.validateSchema(response, currentPhase)) {
        return this.handleSchemaViolation(response, currentPhase);
      }
      
      // Save the response for history
      await this.saveResponseToHistory(response, projectId);
      
      // Process based on action type and phase
      switch (response.a) {
        case 'GEN':
          return this.handleGenerateAction(response, projectId);
        case 'UPD':
          return this.handleUpdateAction(response, projectId);
        case 'REQ':
          return this.handleRequestAction(response, projectId);
        case 'ACK':
          return this.handleAcknowledgeAction(response);
        case 'ERR':
          return this.handleErrorAction(response);
        default:
          return {
            success: false,
            error: `Unknown action type: ${response.a}`
          };
      }
    } catch (error) {
      // Handle potential truncation or malformed JSON
      return this.handleInvalidResponse(jsonString, error);
    }
  }
  
  /**
   * Validate response schema
   * @param {Object} response - Parsed response object
   * @param {string} phase - Current project phase
   * @returns {boolean} Validation result
   */
  validateSchema(response, phase) {
    // Basic validation - check for required fields
    if (!response.v || !response.p || !response.a || !response.c) {
      return false;
    }
    
    // Check phase matches (allow 'F' for finishing phase to match 'FINISHING')
    const phaseMap = {
      'PLANNING': 'P',
      'DESIGN': 'D',
      'CODING': 'C',
      'REFINEMENT': 'R',
      'FINISHING': 'F'
    };
    
    if (response.p !== phaseMap[phase]) {
      return false;
    }
    
    // Validate content based on action type
    switch (response.a) {
      case 'GEN':
        return this.validateGenerateContent(response.c, response.p);
      case 'UPD':
        return this.validateUpdateContent(response.c);
      case 'REQ':
        return this.validateRequestContent(response.c);
      case 'ACK':
        return this.validateAcknowledgeContent(response.c);
      case 'ERR':
        return this.validateErrorContent(response.c);
      default:
        return false;
    }
  }
  
  /**
   * Validate content for 'GEN' action
   * @param {Object} content - Response content
   * @param {string} phase - Response phase
   * @returns {boolean} Validation result
   */
  validateGenerateContent(content, phase) {
    switch (phase) {
      case 'P': // Planning
        return content.cp && Array.isArray(content.cp) && content.seq;
      case 'D': // Design
        return content.fs && Array.isArray(content.fs) && content.bat;
      case 'C': // Coding
        return content.f && Array.isArray(content.f);
      case 'F': // Finishing
        return content.op && Array.isArray(content.op);
      default:
        return false;
    }
  }
  
  /**
   * Validate content for 'UPD' action
   * @param {Object} content - Response content
   * @returns {boolean} Validation result
   */
  validateUpdateContent(content) {
    return content.p && content.o && Array.isArray(content.o);
  }
  
  /**
   * Validate content for 'REQ' action
   * @param {Object} content - Response content
   * @returns {boolean} Validation result
   */
  validateRequestContent(content) {
    return content.q && content.t;
  }
  
  /**
   * Validate content for 'ACK' action
   * @param {Object} content - Response content
   * @returns {boolean} Validation result
   */
  validateAcknowledgeContent(content) {
    return content.m !== undefined;
  }
  
  /**
   * Validate content for 'ERR' action
   * @param {Object} content - Response content
   * @returns {boolean} Validation result
   */
  validateErrorContent(content) {
    return content.e && content.d;
  }
  
  /**
   * Handle schema validation failures
   * @param {Object} response - Invalid response object
   * @param {string} phase - Current project phase
   * @returns {Object} Error result
   */
  handleSchemaViolation(response, phase) {
    return {
      success: false,
      error: `Invalid response format for ${phase} phase`,
      details: 'Response does not match expected schema',
      response
    };
  }
  
  /**
   * Save response to history
   * @param {Object} response - Response to save
   * @param {string} projectId - Project ID
   * @returns {Promise<string>} Response ID
   */
  async saveResponseToHistory(response, projectId) {
    try {
      const responseRecord = {
        id: crypto.randomUUID(),
        projectId,
        phase: response.p,
        action: response.a,
        content: response,
        timestamp: Date.now()
      };
      
      return await this.storage.saveResponse(responseRecord);
    } catch (error) {
      console.error('Error saving response history:', error);
      // Continue processing even if history saving fails
      return null;
    }
  }
  
  /**
   * Handle 'GEN' action type
   * @param {Object} response - Response with GEN action
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async handleGenerateAction(response, projectId) {
    switch (response.p) {
      case 'P': // Planning
        return this.processPlanningResponse(response.c, projectId);
      case 'D': // Design
        return this.processDesignResponse(response.c, projectId);
      case 'C': // Coding
        return this.processCodingResponse(response.c, projectId);
      case 'F': // Finishing
        return this.processFinishingResponse(response.c, projectId);
      default:
        return {
          success: false,
          error: `Invalid phase for generate action: ${response.p}`
        };
    }
  }
  
  /**
   * Process planning phase response
   * @param {Object} content - Response content
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async processPlanningResponse(content, projectId) {
    try {
      // Extract components
      const components = content.cp;
      const implementationSequence = content.seq;
      const totalEstimatedLOC = content.est;
      
      // Save components
      for (const component of components) {
        const componentData = {
          id: component.id,
          projectId,
          name: component.n,
          description: component.d,
          dependencies: component.dep || [],
          files: component.f.map(f => f.p)
        };
        
        await this.storage.saveComponent(componentData);
        
        // Save files for this component
        for (const file of component.f) {
          const fileData = {
            projectId,
            path: file.p,
            estimatedLoc: file.e,
            description: file.d,
            componentId: component.id,
            language: this.detectLanguageFromPath(file.p)
          };
          
          await this.storage.saveFile(fileData);
        }
      }
      
      // Update project with planning data
      const project = await this.storage.getProject(projectId);
      if (project) {
        const updatedProject = {
          ...project,
          totalEstimatedLOC,
          implementationSequence,
          planningComplete: true,
          updated: Date.now()
        };
        
        await this.storage.updateProject(updatedProject);
      }
      
      return {
        success: true,
        components: components.length,
        files: components.reduce((sum, component) => sum + component.f.length, 0),
        totalEstimatedLOC
      };
    } catch (error) {
      console.error('Error processing planning response:', error);
      return {
        success: false,
        error: `Failed to process planning response: ${error.message}`
      };
    }
  }
  
  /**
   * Process design phase response
   * @param {Object} content - Response content
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async processDesignResponse(content, projectId) {
    try {
      // Extract file specifications
      const fileSpecs = content.fs;
      const batches = content.bat;
      
      // Update files with design data
      for (const spec of fileSpecs) {
        // Find file by path
        const file = await this.storage.getFileByPath(projectId, spec.p);
        
        if (file) {
          // Update file with design specifications
          const updatedFile = {
            ...file,
            estimatedLoc: spec.e || file.estimatedLoc,
            description: spec.d || file.description,
            exports: spec.exp || [],
            imports: spec.imp || [],
            algorithms: spec.alg || []
          };
          
          await this.storage.saveFile(updatedFile);
        } else {
          // Create new file if it doesn't exist
          const fileData = {
            projectId,
            path: spec.p,
            estimatedLoc: spec.e,
            description: spec.d,
            exports: spec.exp || [],
            imports: spec.imp || [],
            algorithms: spec.alg || [],
            language: this.detectLanguageFromPath(spec.p)
          };
          
          await this.storage.saveFile(fileData);
        }
      }
      
      // Save implementation batches
      for (const batch of batches) {
        // Convert file paths to file IDs
        const fileIds = [];
        for (const filePath of batch.f) {
          const file = await this.storage.getFileByPath(projectId, filePath);
          if (file) {
            fileIds.push(file.id);
          }
        }
        
        // Calculate total LOC for the batch
        const totalLoc = fileIds.reduce(async (sum, fileId) => {
          const file = await this.storage.getFile(fileId);
          return sum + (file ? file.estimatedLoc : 0);
        }, 0);
        
        // Create batch record
        const batchData = {
          id: batch.id || `batch_${crypto.randomUUID().substring(0, 8)}`,
          projectId,
          name: `Batch ${batch.p}`,
          files: fileIds,
          status: 'pending',
          priority: batch.p,
          totalLoc: await totalLoc
        };
        
        await this.storage.saveBatch(batchData);
      }
      
      // Update project with design data
      const project = await this.storage.getProject(projectId);
      if (project) {
        const updatedProject = {
          ...project,
          designComplete: true,
          currentPhase: 'CODING',
          updated: Date.now()
        };
        
        await this.storage.updateProject(updatedProject);
      }
      
      return {
        success: true,
        files: fileSpecs.length,
        batches: batches.length
      };
    } catch (error) {
      console.error('Error processing design response:', error);
      return {
        success: false,
        error: `Failed to process design response: ${error.message}`
      };
    }
  }
  
  /**
   * Process coding phase response
   * @param {Object} content - Response content
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async processCodingResponse(content, projectId) {
    const result = {
      success: true,
      files: [],
      stats: {
        filesProcessed: 0,
        totalLoc: 0
      }
    };
    
    // Process each file in the response
    for (const fileData of content.f) {
      try {
        // Check if file exists
        let file = await this.storage.getFileByPath(projectId, fileData.p);
        
        if (!file) {
          // Create new file if it doesn't exist
          file = {
            id: crypto.randomUUID(),
            projectId,
            path: fileData.p,
            language: fileData.l || this.detectLanguageFromPath(fileData.p),
            estimatedLoc: fileData.e || 0,
            actualLoc: 0,
            currentVersionId: null,
            description: '',
            exports: [],
            imports: []
          };
        }
        
        // Update file metadata
        file.actualLoc = fileData.a || this.countLinesOfCode(fileData.c);
        file.language = fileData.l || file.language;
        
        // Extract exports and imports if not present
        if (!file.exports || file.exports.length === 0) {
          file.exports = this.extractExports(fileData.c, file.language);
        }
        
        if (!file.imports || file.imports.length === 0) {
          file.imports = this.extractImports(fileData.c, file.language);
        }
        
        // Save file metadata
        await this.storage.saveFile(file);
        
        // Create new file version
        const fileContent = fileData.c;
        const currentVersion = file.currentVersionId 
          ? await this.storage.getFileContent(file.id, file.currentVersionId)
          : null;
        
        const newVersion = currentVersion ? (currentVersion.version + 1) : 1;
        
        const contentId = await this.storage.saveFileContent(
          file.id,
          fileContent,
          newVersion
        );
        
        // Update current version reference
        file.currentVersionId = contentId;
        await this.storage.saveFile(file);
        
        // Update result stats
        result.stats.filesProcessed++;
        result.stats.totalLoc += file.actualLoc;
        
        // Add to processed files
        result.files.push({
          id: file.id,
          path: file.path,
          language: file.language,
          actualLoc: file.actualLoc
        });
      } catch (error) {
        console.error(`Error processing file ${fileData.p}:`, error);
        
        // Add error but continue processing other files
        if (!result.errors) result.errors = [];
        result.errors.push({
          path: fileData.p,
          error: error.message
        });
      }
    }
    
    return result;
  }
  
  /**
   * Process finishing phase response
   * @param {Object} content - Response content
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async processFinishingResponse(content, projectId) {
    try {
      const optimizations = content.op;
      const appliedOptimizations = [];
      const result = {
        success: true,
        optimizations: 0,
        details: []
      };
      
      // Apply each optimization
      for (const optimization of optimizations) {
        try {
          // Get file by path
          const file = await this.storage.getFileByPath(projectId, optimization.p);
          
          if (!file) {
            throw new Error(`File not found: ${optimization.p}`);
          }
          
          // Get current file content
          const currentContentObj = await this.storage.getFileContent(file.id);
          if (!currentContentObj) {
            throw new Error(`File content not found for ${optimization.p}`);
          }
          
          const currentContent = currentContentObj.content;
          
          // Apply optimization
          let updatedContent = currentContent;
          
          // Replace specified lines
          if (optimization.s !== undefined && optimization.e !== undefined && optimization.c) {
            updatedContent = this.replaceLines(
              updatedContent, 
              optimization.s, 
              optimization.e, 
              optimization.c
            );
          }
          
          // Store new version
          const newVersion = currentContentObj.version + 1;
          const contentId = await this.storage.saveFileContent(
            file.id,
            updatedContent,
            newVersion
          );
          
          // Update file metadata
          file.currentVersionId = contentId;
          file.actualLoc = this.countLinesOfCode(updatedContent);
          
          await this.storage.saveFile(file);
          
          appliedOptimizations.push({
            path: optimization.p,
            type: optimization.t,
            description: optimization.d
          });
        } catch (error) {
          console.error(`Error applying optimization to ${optimization.p}:`, error);
          
          // Add error but continue with other optimizations
          if (!result.errors) result.errors = [];
          result.errors.push({
            path: optimization.p,
            error: error.message
          });
        }
      }
      
      // Update project with finishing data
      const project = await this.storage.getProject(projectId);
      if (project) {
        const updatedProject = {
          ...project,
          finishingComplete: true,
          updated: Date.now()
        };
        
        await this.storage.updateProject(updatedProject);
      }
      
      result.optimizations = appliedOptimizations.length;
      result.details = appliedOptimizations;
      
      return result;
    } catch (error) {
      console.error('Error processing finishing response:', error);
      return {
        success: false,
        error: `Failed to process finishing response: ${error.message}`
      };
    }
  }
  
  /**
   * Handle 'UPD' action type
   * @param {Object} response - Response with UPD action
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Processing result
   */
  async handleUpdateAction(response, projectId) {
    // Pass the update operation to the result for applying in the component
    return {
      success: true,
      updateOperation: response.c
    };
  }
  
  /**
   * Handle 'REQ' action type (AI requesting more information)
   * @param {Object} response - Response with REQ action
   * @param {string} projectId - Project ID
   * @returns {Object} Processing result with request info
   */
  async handleRequestAction(response, projectId) {
    return {
      success: true,
      requestType: 'information',
      question: response.c.q,
      type: response.c.t
    };
  }
  
  /**
   * Handle 'ACK' action type (acknowledgement)
   * @param {Object} response - Response with ACK action
   * @returns {Object} Processing result
   */
  async handleAcknowledgeAction(response) {
    return {
      success: true,
      message: response.c.m
    };
  }
  
  /**
   * Handle 'ERR' action type (error response from AI)
   * @param {Object} response - Response with ERR action
   * @returns {Object} Processing result with error info
   */
  async handleErrorAction(response) {
    return {
      success: false,
      error: response.c.e,
      details: response.c.d
    };
  }
  
  /**
   * Handle invalid response (JSON parsing error)
   * @param {string} jsonString - Raw JSON string
   * @param {Error} error - Parsing error
   * @returns {Object} Processing result with error info
   */
  handleInvalidResponse(jsonString, error) {
    // Check if response was truncated
    if (this.isTruncatedJSON(jsonString)) {
      return this.handleTruncation(jsonString);
    }
    
    return {
      success: false,
      error: `Invalid JSON response: ${error.message}`
    };
  }
  
  /**
   * Check if JSON string is truncated
   * @param {string} jsonString - JSON string to check
   * @returns {boolean} True if truncated
   */
  isTruncatedJSON(jsonString) {
    // Check if string ends with a complete structure
    const lastBrace = jsonString.lastIndexOf('}');
    const lastBracket = jsonString.lastIndexOf(']');
    
    // If no closing braces, likely truncated
    if (lastBrace === -1 && lastBracket === -1) {
      return true;
    }
    
    try {
      // Try to parse the JSON
      JSON.parse(jsonString);
      return false; // If we got here, JSON is valid
    } catch (error) {
      // If error occurs at the end of the string, likely truncated
      const position = this.getErrorPosition(error);
      return position > jsonString.length - 20;
    }
  }
  
  /**
   * Get error position from JSON parse error
   * @param {Error} error - JSON parsing error
   * @returns {number} Position in string
   */
  getErrorPosition(error) {
    const match = error.message.match(/position\s+(\d+)/);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
    return -1;
  }
  
  /**
   * Handle truncated JSON response
   * @param {string} jsonString - Truncated JSON string
   * @returns {Object} Processing result with continuation info
   */
  handleTruncation(jsonString) {
    // Find the last complete line of code
    const lastCompleteLine = this.findLastCompleteLine(jsonString);
    const filePath = this.findFilePath(jsonString);
    
    return {
      success: false,
      truncated: true,
      lastCompleteLine,
      filePath,
      continuationPrompt: this.generateContinuationPrompt(lastCompleteLine, filePath)
    };
  }
  
  /**
   * Find the last complete line in truncated JSON
   * @param {string} jsonString - Truncated JSON string
   * @returns {string} Last complete line
   */
  findLastCompleteLine(jsonString) {
    // Look for file content in the JSON
    const contentMatch = /"c"\s*:\s*"(.+?)(?=",|"})/.exec(jsonString);
    
    if (!contentMatch) {
      // Fall back to the last line
      const lines = jsonString.split('\n');
      return lines[lines.length - 1];
    }
    
    // Parse the content
    const content = contentMatch[1];
    const lines = content.split('\\n');
    
    // Return the last complete line
    return lines[lines.length - 1];
  }
  
  /**
   * Find file path in truncated JSON
   * @param {string} jsonString - Truncated JSON string
   * @returns {string} File path or empty string
   */
  findFilePath(jsonString) {
    // Look for file path in the JSON
    const pathMatch = /"p"\s*:\s*"(.+?)(?=",)/.exec(jsonString);
    
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    
    return '';
  }
  
  /**
   * Generate a continuation prompt
   * @param {string} lastCompleteLine - Last complete line
   * @param {string} filePath - File path
   * @returns {string} Continuation prompt
   */
  generateContinuationPrompt(lastCompleteLine, filePath) {
    return `
Continue from this point:

LAST COMPLETE LINE:
${lastCompleteLine}

FILE:
${filePath || 'unknown'}

Start with this exact line and continue implementation.
Return ONLY the JSON with no explanation text.
`;
  }
  
  /**
   * Replace lines in a string
   * @param {string} content - Original content
   * @param {number} start - Start line (1-based)
   * @param {number} end - End line (1-based)
   * @param {string} newContent - Replacement content
   * @returns {string} Updated content
   */
  replaceLines(content, start, end, newContent) {
    const lines = content.split('\n');
    
    // Validate line range
    if (start < 1 || end > lines.length || start > end) {
      throw new Error(`Invalid line range: ${start}-${end} (max: ${lines.length})`);
    }
    
    // Replace lines
    const newLines = newContent.split('\n');
    const result = [
      ...lines.slice(0, start - 1),
      ...newLines,
      ...lines.slice(end)
    ];
    
    return result.join('\n');
  }
  
  /**
   * Count lines of code in a string
   * @param {string} content - Code content
   * @returns {number} Line count
   */
  countLinesOfCode(content) {
    return content.split('\n').length;
  }
  
  /**
   * Extract exports from file content
   * @param {string} content - File content
   * @param {string} language - File language
   * @returns {Array} Extracted exports
   */
  extractExports(content, language) {
    // Simple export detection - should be enhanced with proper parsing
    const exports = [];
    
    if (language === 'javascript' || language === 'js') {
      // Match module.exports and named exports
      const moduleExports = /module\.exports\s*=\s*{([^}]*)}/g.exec(content);
      if (moduleExports) {
        const exportItems = moduleExports[1].split(',');
        for (const item of exportItems) {
          const trimmed = item.trim();
          if (trimmed) exports.push(trimmed);
        }
      }
      
      // Match ES6 exports
      const exportMatches = content.match(/export\s+(const|let|var|function|class)\s+(\w+)/g);
      if (exportMatches) {
        for (const match of exportMatches) {
          const parts = match.split(/\s+/);
          if (parts.length >= 3) exports.push(parts[2]);
        }
      }
      
      // Match default export
      const defaultExport = /export\s+default\s+(\w+)/g.exec(content);
      if (defaultExport && defaultExport[1]) {
        exports.push(`default (${defaultExport[1]})`);
      }
    }
    
    return exports;
  }
  
  /**
   * Extract imports from file content
   * @param {string} content - File content
   * @param {string} language - File language
   * @returns {Array} Extracted imports
   */
  extractImports(content, language) {
    // Simple import detection - should be enhanced with proper parsing
    const imports = [];
    
    if (language === 'javascript' || language === 'js') {
      // Match require statements
      const requireMatches = content.match(/require\(['"]([^'"]+)['"]\)/g);
      if (requireMatches) {
        for (const match of requireMatches) {
          const path = match.match(/require\(['"]([^'"]+)['"]\)/)[1];
          imports.push(path);
        }
      }
      
      // Match ES6 imports
      const importMatches = content.match(/import\s+.*?from\s+['"]([^'"]+)['"]/g);
      if (importMatches) {
        for (const match of importMatches) {
          const path = match.match(/from\s+['"]([^'"]+)['"]/)[1];
          imports.push(path);
        }
      }
    }
    
    return imports;
  }
  
  /**
   * Detect language from file path
   * @param {string} path - File path
   * @returns {string} Detected language
   */
  detectLanguageFromPath(path) {
    const extension = path.split('.').pop().toLowerCase();
    
    const extensionMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'md': 'markdown',
      'py': 'python',
      'rb': 'ruby',
      'java': 'java',
      'php': 'php',
      'go': 'go',
      'rust': 'rust',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp'
    };
    
    return extensionMap[extension] || extension;
  }
}

export default ResponseProcessor;